import React, { useEffect, useState } from "react";
import { useSignupExpertStore } from "@src/store/signupExpertStore";
import { useNavigate } from "react-router-dom";
import { styled } from "styled-components";
import { Common } from "@src/styles/Common";

import MgInput from "../../components/input/MgInput";
import SignupProfileUploadContainer from "../../components/SignupExpert/Containers/SignupProfileUploader";
import ExpertSelect from "../../components/SignupExpert/ExpertSelect";
import MgDialog from "../../components/Dialog";
import AgreementTermBox from "../../components/signUp/AgreementTermBox";

import { validateInputText } from "../../util/validation";
import { SignupExpertParams, getPartnerCodeDuplicated, useJoinExpert, CounselingCategory } from "@src/api/expert/expertApi";

import missgoUnifiedLogo from "../../assets/missgoUnifiedLogo.png";
import Cancel from "../../assets/Signup/cancel";
import WaitConfirm from "../../assets/Signup/waitConfirm.png";
import { transferToNative } from "@src/lib/WebAppBridge";

const SignUpExpert = () => {
    const navigate = useNavigate();

    const { form, validationErrors, isPartnerCodeValid, isAgreementTermsChecked, setPartialState } = useSignupExpertStore();

    const [showPendingPopup, setShowPendingPopup] = useState(false);
    const [joinError, setJoinError] = useState<string>("");
    const [canSubmit, setCanSubmit] = useState(false);

    // 관리자 승인 대기 팝업 이후 인터페이스 호출하기 위한 임시 토큰 저장용
    const [tempToken, setTempToken] = useState<{ accessToken: string; refreshToken: string } | null>(null);

    const { mutate: joinExpert, isLoading: isJoining } = useJoinExpert();

    // 경매대행(매수신청대리인) - 관리자 승인 대기 팝업 확인 버튼
    const handlePendingConfirm = () => {
        if (tempToken) {
            transferToNative(
                JSON.stringify({
                    accessToken: tempToken.accessToken,
                    refreshToken: tempToken.refreshToken,
                }),
                "onRedirectHomeWithToken",
            );
        } else {
            setJoinError("회원가입에 실패했습니다.");
        }
    };

    const handleChange = (key: keyof SignupExpertParams, e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setPartialState({
            form: { ...form, [key]: value },
        });
        const validMsg = validateInputText(key, value);
        if (validMsg !== null) {
            setPartialState({
                validationErrors: { ...validationErrors, [key]: validMsg },
            });
        } else {
            const updated = { ...validationErrors };
            delete updated[key];
            setPartialState({
                validationErrors: updated,
            });
        }
    };

    // 회원가입 완료 후 승인 대기 팝업 띄우기 (delegation 선택 시에만)
    const openPendingPopup = () => {
        setShowPendingPopup(true);
    };

    const validateAndSubmitForm = (runType: "validate" | "submit") => {
        const missingKey = REQUIRED_KEYS.find((k) => isEmpty(form[k]));
        if (missingKey) {
            if (runType === "submit") {
                setJoinError(`${KEY_TO_KR[missingKey]}(*필수)을 입력해주세요.`);
            } else {
                setCanSubmit(false);
            }
            return;
        }

        // input 필수 항목 validation 여부
        if (Object.keys(validationErrors).length > 0) {
            if (runType === "submit") {
                setJoinError(
                    `${Object.keys(validationErrors)
                        .map((key) => KEY_TO_KR[key as keyof typeof KEY_TO_KR])
                        .join(", ")}을 올바르게 입력해주세요.`,
                );
            } else {
                setCanSubmit(false);
            }
            return;
        }

        // 매수신청대리인(경매대행) - 등록증 업로드 여부
        const isDelegationCertUrlNeed = form.counselingCategories.includes("매수신청대리인") && !form.auctionAgentCertFileUrl;
        if (isDelegationCertUrlNeed) {
            if (runType === "submit") {
                setJoinError("[경매대행] 선택 시, 매수신청대리인 등록증을 업로드해주세요.");
            } else {
                setCanSubmit(false);
            }
            return;
        }

        // 매수신청대리인(경매대행) - 중개사무소 정보 입력 여부
        const isDelegationOfficeInfoNeed = form.counselingCategories.includes("매수신청대리인") && !form.officeInfo;
        if (isDelegationOfficeInfoNeed) {
            if (runType === "submit") {
                setJoinError("[경매대행] 선택 시, 중개사무소 정보를 입력해주세요.");
            } else {
                setCanSubmit(false);
            }
            return;
        }

        // 중개 - 중개사무소 입력 여부
        const isBrokerageOfficeInfoNeed = form.counselingCategories.includes("중개") && !form.officeInfo;
        if (isBrokerageOfficeInfoNeed) {
            if (runType === "submit") {
                setJoinError("[중개] 선택 시, 중개사무소 정보를 입력해주세요.");
            } else {
                setCanSubmit(false);
            }
            return;
        }

        // counselingCategories 입력 여부 - career 입력 여부
        const isCounselingCategoriesNotValid =
            form.counselingCategories.length > 0 &&
            (!form.career ||
                Object.keys(form.career).length !== form.counselingCategories.length ||
                Object.values(form.career).some((value) => value === 0));
        if (isCounselingCategoriesNotValid) {
            if (runType === "submit") {
                setJoinError(`선택하신 전문가의 경력(*시작연도)을 입력해주세요.`);
            } else {
                setCanSubmit(false);
            }
            return;
        }

        // 약관동의 - 필수 항목 동의 여부
        if (!isAgreementTermsChecked) {
            if (runType === "submit") {
                setJoinError("필수 약관에 동의해주세요.");
            } else {
                setCanSubmit(false);
            }
            return;
        }

        // 추천인 코드 입력 여부
        const isPartnerNotValid = form.partnerCode && form.partnerCode.trim() !== "" && !isPartnerCodeValid;
        if (isPartnerNotValid) {
            if (runType === "submit") {
                setJoinError("추천인 코드를 확인해주세요.");
            } else {
                setCanSubmit(false);
            }
            return;
        }
        if (runType === "validate") {
            setCanSubmit(true);
            return;
        }

        if (runType === "submit") {
            // 인증받은 휴대전화 번호가 상태에서 사라진 경우
            if (!form.phone || form.phone.trim().length === 0) {
                setJoinError("인증된 휴대전화 번호가 만료되었습니다. 다시 인증해주세요.");
                setTimeout(() => {
                    navigate("/login");
                }, 3000);
                return;
            }

            doJoin();
        }
    };

    const doJoin = async () => {
        const params: SignupExpertParams = {
            ...form,
            isAuctionAgent: form.counselingCategories.includes("매수신청대리인") && !!form.auctionAgentCertFileUrl,
        };
        joinExpert(params, {
            onSuccess: (data) => {
                if (data && data.success === false) {
                    setJoinError((prev) => (prev ? prev : (data as any).message || "회원가입에 실패했습니다."));
                    return;
                }
                if (data && data.success === true) {
                    if (form.counselingCategories.includes("매수신청대리인")) {
                        openPendingPopup();
                        setTempToken(data.token);
                        return;
                    } else {
                        if (data && data.token?.accessToken && data.token?.refreshToken) {
                            transferToNative(
                                JSON.stringify({
                                    accessToken: data.token.accessToken,
                                    refreshToken: data.token.refreshToken,
                                }),
                                "onRedirectHomeWithToken",
                            );

                            console.log("Bridge Request Success)");
                            return;
                        }
                    }
                }
            },
            onError: (error: any) => {
                setJoinError((prev) => (prev ? prev : error?.response?.data?.message || "회원가입에 실패했습니다."));
            },
        });
    };

    const handleCheckPartnerCode = async () => {
        if (!form.partnerCode) {
            setJoinError("추천인 코드를 입력해주세요.");
            return;
        }
        const isDuplicated = await getPartnerCodeDuplicated(form.partnerCode ?? "");

        setPartialState({
            isPartnerCodeValid: !isDuplicated,
        });
    };

    const handleCancel = () => navigate("/usertype");

    useEffect(() => {
        validateAndSubmitForm("validate");
    }, [form, isPartnerCodeValid]);

    useEffect(() => {
    }, [validationErrors]);

    return (
        <Background>
            <Expert_Container>
                <TitleBox>
                    <TitleHeader>
                        <img className="logo" alt="미스고 통합 로고" src={missgoUnifiedLogo} width={80} height={36} />
                        <div onClick={handleCancel} style={{ cursor: "pointer" }}>
                            <Cancel />
                        </div>
                    </TitleHeader>
                    <SubTitleStyle>
                        <h3 className="title">미스고 통합 회원가입</h3>
                    </SubTitleStyle>
                    <TextTitle>
                        가입 시, 미스고 통합회원으로
                        <br />
                        부동산 및 부동산지도를 함께 이용 가능해요.
                    </TextTitle>
                </TitleBox>

                {/* 닉네임 */}
                <InputBox>
                    <InputHeader>
                        <div className="star">*</div>
                        <div className="title">닉네임</div>
                    </InputHeader>
                    <MgInput
                        width="100%"
                        value={form.name}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange("name", e)}
                        placeholder="한글, 영문 2~10자 입력해주세요."
                        noBorder
                        bgColor="#FBF8F8"
                    />
                    {validationErrors.name && <ErrorMsg>{validationErrors.name}</ErrorMsg>}
                </InputBox>

                {/* 이메일 */}
                <InputBox>
                    <InputHeader>
                        <div className="star">*</div>
                        <div className="title">이메일</div>
                    </InputHeader>
                    <MgInput
                        width="100%"
                        value={form.email}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange("email", e)}
                        placeholder="missgo@auction.com"
                        noBorder
                        bgColor="#FBF8F8"
                    />
                    {validationErrors.email && <ErrorMsg>{validationErrors.email}</ErrorMsg>}
                </InputBox>

                {/* 프로필 사진 */}
                <InputBox className="profile-box">
                    <InputHeader>
                        <div className="star">*</div>
                        <div className="title">프로필 사진</div>
                    </InputHeader>
                    <div className="profile-upload-wrapper">
                        <SignupProfileUploadContainer />
                    </div>
                </InputBox>

                {/* 추천인 코드 */}
                <InputBox>
                    <InputHeader>
                        <div className="title">본인 추천인 코드</div>
                        <CheckPartnerCodeButton type="button" onClick={handleCheckPartnerCode}>
                            중복검사
                        </CheckPartnerCodeButton>
                    </InputHeader>
                    <div className="subTitle">사용자가 해당 추천인 코드를 사용해 혜택을 받을 수 있어요!</div>
                    <MgInput
                        width="100%"
                        value={form.partnerCode}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            if (isPartnerCodeValid !== null) {
                                setPartialState({
                                    isPartnerCodeValid: null,
                                });
                            }
                            setPartialState({
                                form: { ...form, partnerCode: e.target.value },
                            });
                        }}
                        placeholder="추천인 코드를 입력해주세요."
                        noBorder
                        bgColor="#FBF8F8"
                        aria-describedby="partner-code-msg"
                    />
                    {isPartnerCodeValid !== null && (
                        <PartnerCodeMsg id="partner-code-msg" data-valid={isPartnerCodeValid ? "yes" : "no"}>
                            {isPartnerCodeValid ? "사용 가능한 코드입니다." : "중복된 코드입니다. 다른 코드를 입력해주세요."}
                        </PartnerCodeMsg>
                    )}
                </InputBox>

                {/* 원하는 전문가 */}
                <InputBox>
                    <InputHeader>
                        <div className="star">*</div>
                        <div className="title">원하는 전문가 (복수선택 가능)</div>
                    </InputHeader>
                    <ExpertSelect />
                </InputBox>

                {/* 계좌번호 정보 */}
                <InputBox>
                    <InputHeader>
                        <div className="star">*</div>
                        <div className="title">계좌번호 정보</div>
                    </InputHeader>
                    <MgInput
                        width="100%"
                        value={form.bankName}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange("bankName", e)}
                        placeholder="은행명"
                        noBorder
                        bgColor="#FBF8F8"
                    />
                    {validationErrors.bankName && <ErrorMsg>{validationErrors.bankName}</ErrorMsg>}

                    <MgInput
                        width="100%"
                        value={form.bankAccount}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange("bankAccount", e)}
                        placeholder="계좌번호 입력"
                        noBorder
                        bgColor="#FBF8F8"
                        inputMode="numeric"
                    />
                    {validationErrors.bankAccount && <ErrorMsg>{validationErrors.bankAccount}</ErrorMsg>}
                </InputBox>

                {/* 경력 연도 */}
                {form.counselingCategories.length > 0 && (
                    <InputBox>
                        <InputHeader>
                            <div className="star">*</div>
                            <div className="title">경력 (시작 연도를 입력해주세요)</div>
                        </InputHeader>

                        {COUNCELING_CATEGORIES_ORDER.filter((category) =>
                            form.counselingCategories.includes(category as CounselingCategory),
                        ).map((category) => (
                            <YearRow key={category}>
                                <YearLabel>{category === "매수신청대리인" ? "경매대행" : category}</YearLabel>
                                <YearInputWrap>
                                    <MgInput
                                        width="100%"
                                        maxLength={4}
                                        inputMode="numeric"
                                        value={
                                            form.career
                                                ? form.career[category as keyof typeof form.career] === 0
                                                    ? ""
                                                    : form.career[category as keyof typeof form.career]
                                                : ""
                                        }
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                            if (e.target.value.length > 0 && isNaN(Number(e.target.value))) {
                                                setPartialState({
                                                    form: {
                                                        ...form,
                                                        career: {
                                                            ...(form.career as Record<CounselingCategory, number>),
                                                            [category]: 0,
                                                        },
                                                    },
                                                });
                                                return;
                                            }
                                            setPartialState({
                                                form: {
                                                    ...form,
                                                    career: {
                                                        ...(form.career as Record<CounselingCategory, number>),
                                                        [category]: e.target.value ? Number(e.target.value) : 0,
                                                    },
                                                },
                                            });
                                        }}
                                        placeholder="YYYY"
                                        noBorder
                                        bgColor="#FBF8F8"
                                    />
                                    <YearSuffix>년</YearSuffix>
                                </YearInputWrap>
                            </YearRow>
                        ))}
                    </InputBox>
                )}

                {/* 자격 (선택) */}
                <InputBox>
                    <InputHeader>
                        <div className="title">자격 (선택)</div>
                    </InputHeader>
                    <TextareaWrapper>
                        <MgInput
                            as="textarea"
                            width="100%"
                            value={form.qualification}
                            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                                setPartialState({ form: { ...form, qualification: e.target.value } })
                            }
                            placeholder="다른 자격 사항이 있으면 작성해주세요. (최대 200자)"
                            noBorder
                            bgColor="#FBF8F8"
                            height="170px"
                            maxLength={200}
                        />
                        <CharCount>{form.qualification?.length ?? 0}/200</CharCount>
                    </TextareaWrapper>
                </InputBox>

                {/* 소개 (선택) */}
                <InputBox>
                    <InputHeader>
                        <div className="title">소개 (선택)</div>
                    </InputHeader>
                    <TextareaWrapper>
                        <MgInput
                            as="textarea"
                            width="100%"
                            value={form.introduction}
                            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                                setPartialState({ form: { ...form, introduction: e.target.value } })
                            }
                            placeholder="소개 글을 작성해주세요. (최대 500자)"
                            noBorder
                            bgColor="#FBF8F8"
                            height="170px"
                            maxLength={500}
                        />
                        <CharCount>{form.introduction?.length ?? 0}/500</CharCount>
                    </TextareaWrapper>
                </InputBox>

                {/* 약관 동의 */}
                <AgreementTermBox
                    onCheckRequiredTerms={(isChecked) => {
                        setPartialState({ isAgreementTermsChecked: isChecked });
                    }}
                    onCheckMarketingTerms={(isChecked) => {
                        setPartialState({ form: { ...form, isAgreedMarketing: isChecked } });
                    }}
                />

                {/* 가입 완료 버튼 */}
                <BtnStyle className={canSubmit ? "active" : ""} onClick={() => validateAndSubmitForm("submit")}>
                    {isJoining ? "가입 중..." : "회원가입 완료"}
                </BtnStyle>

                {joinError && (
                    <MgDialog
                        addDialogStyle={{ zIndex: 5000 }}
                        open={true}
                        onCloseView={() => setJoinError("")}
                        contents={
                            <div style={{ textAlign: "center", padding: "24px 0", width: "240px" }}>
                                <p style={{ fontSize: 16, lineHeight: "160%" }}>{joinError}</p>
                            </div>
                        }
                        confirmText="확인"
                        confirmStyle={{ fontSize: 16 }}
                        onClickSubmit={() => setJoinError("")}
                    />
                )}

                {/* 관리자 승인 대기중 팝업 */}
                <MgDialog
                    addDialogStyle={{ zIndex: 4000 }}
                    open={showPendingPopup}
                    onCloseView={handlePendingConfirm}
                    contents={
                        <PendingPopupContents>
                            <p className="title">관리자 승인 대기중</p>
                            <img className="img" src={WaitConfirm} alt="승인 대기중" />
                            <p className="desc">
                                매수신청대리인의 경우
                                <br />
                                관리자의 별도 승인이 필요합니다. <br />
                                승인 후 경매대행을 할 수 있습니다.
                            </p>
                        </PendingPopupContents>
                    }
                    confirmText="확인"
                    confirmStyle={{ fontSize: 16 }}
                />
            </Expert_Container>
        </Background>
    );
};

export default SignUpExpert;

const Background = styled.div`
    position: relative;
    width: 100%;
    height: 100vh;
    background-color: #f5f5f5;
    background-position: center;
    overflow: hidden;

    @midia screen and (max-width: 768px) {
        display: none;
        background-image: none;
        background-color: #ffffff;
    }
`;

const Expert_Container = styled.div`
    display: flex;
    height: 100vh;
    flex-direction: column;
    padding: 24px;
    overflow-y: scroll;
    background: #ffffff;

    .subTitle {
        font-size: 12px;
        font-weight: 400;
        color: #b8b8b8;
        line-height: 140%;
        margin-bottom: 8px;
    }

    @media screen and (min-width: 768px) {
        position: absolute;
        width: 336px;
        height: 700px;
        top: 60px;
        left: 50%;
        transform: translateX(-50%);
        margin-bottom: 60px;
        border-radius: 12px;
        background: #ffffff;
        overflow-y: auto;
        /* 내부 요소 스크롤, 위아래 공간 */
    }
`;

const TitleBox = styled.div`
    display: flex;
    flex-direction: column;
    margin-bottom: 20px;
`;

const TitleHeader = styled.div`
    display: flex;
    justify-content: space-between;
    margin-bottom: 8px;
`;

const SubTitleStyle = styled.p`
    color: #222222;
    font-size: 24px;
    font-weight: 700;
    line-height: 140%;
    @media screen and (max-width: 768px) {
        margin-top: 0px;
        margin-bottom: 0px;
    }
`;

const TextTitle = styled.p`
    font-size: 12px;
    font-weight: 400;
    color: #9da6b3;
    line-height: 140%;
`;

const InputHeader = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 4px;
    margin-bottom: 8px;

    .star {
        color: ${Common.colors.notiColor};
    }

    .title {
        color: ${Common.colors.normalTextColor};
        font-size: 16px;
        font-weight: 400;
        line-height: 140%;
    }
`;

const InputBox = styled.div`
    margin-bottom: 24px;

    &.profile-box {
        .profile-upload-wrapper {
            display: flex;
            justify-content: center;
        }
    }

    & > input + input {
        margin-top: 8px;
    }
`;

const YearRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    width: 100%;
    padding: 10px 0;
`;

const YearLabel = styled.div`
    font-size: 16px;
    font-weight: 700;
    color: #0c002c;
`;

const YearInputWrap = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100px;
`;

const YearSuffix = styled.span`
    font-size: 16px;
    color: #140c2c;
`;

const BtnStyle = styled.button`
    padding: 12px 14px;
    width: 100%;
    border-radius: 12px;
    background: #c3c3c3;
    color: #ffffff;
    font-weight: 400;
    font-size: 18px;
    cursor: pointer;

    &.active {
        background: ${Common.colors.aucMarkerColor};
    }

    &:disabled {
        cursor: not-allowed;
        opacity: 0.8;
    }

    @media screen and (max-width: 768px) {
        width: 100%;
    }
`;

const CheckPartnerCodeButton = styled.button`
    color: ${Common.colors.blue};
    text-decoration: underline;
    padding: 5px 8px;
    border-radius: 4px;
    font-size: 12px;
    line-height: 140%;
    font-weight: 400;
    margin-left: auto;
    white-space: nowrap;
    background: transparent;
    border: none;
    cursor: pointer;

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
        text-decoration: none;
    }
`;

const TextareaWrapper = styled.div`
    position: relative;
    display: block;
    width: 100%;
    max-width: 336px;
    min-width: 0;
    @media screen and (max-width: 768px) {
        max-width: 100%;
        width: 100%;
    }
`;

const CharCount = styled.span`
    position: absolute;
    bottom: 12px;
    right: 14px;
    font-size: 12px;
    font-weight: 400;
    line-height: 140%;
    color: #c9c9c9;
`;

const ErrorMsg = styled.p`
    margin-top: 6px;
    font-size: 12px;
    line-height: 140%;
    color: ${Common.colors.notiColor};
`;

const PartnerCodeMsg = styled.p`
    margin-top: 6px;
    font-size: 12px;
    line-height: 140%;

    &[data-valid="yes"] {
        color: #2275ee;
    }
    &[data-valid="no"] {
        color: ${Common.colors.notiColor};
    }
`;

const PendingPopupContents = styled.div`
    width: 320px;
    display: flex;
    flex-direction: column;
    align-items: center;

    .title {
        font-size: 22px;
        font-weight: 800;
        line-height: 140%;
        color: ${Common.colors.navyPrimary};
        margin-bottom: 12px;
        text-align: center;
    }

    .img {
        width: 140px;
        height: 140px;
        object-fit: contain;
        margin: 8px 0 12px;
    }

    .desc {
        font-size: 16px;
        font-weight: 400;
        line-height: 150%;
        color: #6b7685;
        text-align: center;
    }
`;

// 1) 필수 필드 키 한정
type InquiredFormKey = Extract<
    keyof SignupExpertParams,
    "name" | "email" | "profileFileUrl" | "bankName" | "bankAccount" | "counselingCategories"
>;

const KEY_TO_KR: Record<InquiredFormKey, string> = {
    name: "닉네임",
    email: "이메일",
    profileFileUrl: "프로필 사진",
    bankName: "은행명",
    bankAccount: "계좌번호",
    counselingCategories: "원하는 전문가",
};

const REQUIRED_KEYS: InquiredFormKey[] = ["name", "email", "profileFileUrl", "bankName", "bankAccount", "counselingCategories"];

const isEmpty = (v: unknown) => {
    if (v == null) return true;
    if (typeof v === "string") return v.trim() === "";
    if (Array.isArray(v)) return v.length === 0;
    return false;
};

export const COUNCELING_CATEGORIES_ORDER: CounselingCategory[] = ["중개", "매물컨설팅", "매수신청대리인", "임장동행"];
